import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { TicketChat } from './TicketChat';
import { FiAlertTriangle, FiArrowRight, FiChevronLeft, FiChevronRight, FiLifeBuoy, FiPlus, FiRefreshCw, FiX } from 'react-icons/fi';
import { useAuth } from 'context/auth-context';
import { EmptyState, PageHeader, SkeletonRows } from 'components/ui';
import { listMyQueries, MyQuery } from './MyQueries.api';
import { TicketStatus, ticketDate, ticketReference } from './TicketDetails';
import { useLinkedTicket } from './useLinkedTicket';
import './UserQueries.css';
import './UserQueriesThread.css';

export function MyQueries({ onCreate }: { onCreate: () => void }) {
  const { auth } = useAuth();
  const [queries, setQueries] = useState<MyQuery[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState<MyQuery | null>(null);
  const [linkError, setLinkError] = useState('');
  const clearLinkedTicket = useLinkedTicket(false, true, ticket => { setLinkError(''); setSelected(ticket); }, setLinkError);
  const close = () => { if (!sending) { setSelected(null); clearLinkedTicket(); } };
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setSelected(null); setQueries([]);
    listMyQueries(page).then(data => {
      if (active) { setQueries(data.queries); setHasMore(data.hasMore); }
    }).catch(() => { if (active) setError('Unable to load your queries. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [auth.id, page, refresh]);
  return <div className="zs-page">
    <PageHeader
      eyebrow="Feedback & support"
      title="My queries"
      subtitle="Track your requests and read responses from our support team."
      actions={<button className="zs-btn zs-btn-primary" onClick={onCreate}><FiPlus />New query</button>}
    />
    {linkError && <p role="alert" className="uq-alert uq-alert-danger mb-5"><FiAlertTriangle aria-hidden="true" /><span>{linkError}</span></p>}
    <section className="overflow-hidden zs-card">
      <div className="zs-card-header">
        <div className="flex items-center min-w-0 gap-3">
          <span className="zs-stat-icon"><FiLifeBuoy /></span>
          <div className="min-w-0"><h2 className="zs-card-title">Your tickets</h2><p className="zs-card-subtitle">Every request you have sent to support</p></div>
        </div>
        <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading} onClick={() => setRefresh(value => value + 1)}><FiRefreshCw className={loading ? 'uq-spin' : ''} />Refresh</button>
      </div>
      {loading ? <div role="status"><span className="sr-only">Loading your queries…</span><SkeletonRows rows={4} /></div>
        : error ? <div role="alert"><EmptyState icon={<FiAlertTriangle />} title="Something went wrong" description={error} action={<button className="zs-btn zs-btn-secondary" onClick={() => setRefresh(value => value + 1)}>Try again</button>} /></div>
        : queries.length === 0 ? <EmptyState icon={<FiLifeBuoy />} title="No queries yet" description="Your submitted support requests will appear here." action={<button className="zs-btn zs-btn-primary" onClick={onCreate}>Submit a query</button>} />
        : <div className="ticket-list">
          {queries.map(query => <button className="ticket-card" key={ticketReference(query)} onClick={() => setSelected(query)}>
            <div className="ticket-card-heading"><strong>{ticketReference(query)}</strong><TicketStatus query={query} /></div>
            <span className="self-start mt-3 zs-badge">{query.feedback_type && query.feedback_type !== 'Unspecified' ? query.feedback_type : 'General inquiry'}</span>
            <p className="uq-message-preview">{query.message}</p>
            <div className="ticket-card-footer">
              <time>{ticketDate(query.created_at)}</time>
              <span className="ticket-card-action">{query.reply_state === 'sent' ? 'View support response' : 'View details'}<FiArrowRight aria-hidden="true" /></span>
            </div>
          </button>)}
        </div>}
      <footer className="zs-table-footer">
        <span>Page {page}</span>
        <div className="flex gap-2">
          <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}><FiChevronLeft />Previous</button>
          <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || !!error || !hasMore} onClick={() => setPage(value => value + 1)}>Next<FiChevronRight /></button>
        </div>
      </footer>
    </section>
    <Modal isOpen={!!selected} onRequestClose={close} shouldCloseOnEsc={!sending} shouldCloseOnOverlayClick={!sending} appElement={document.getElementById('root')} contentLabel="Your query details" className="zs-modal uq-modal" overlayClassName="zs-backdrop uq-overlay">
      {selected && <><header className="uq-modal-header">
        <div className="min-w-0"><div className="zs-eyebrow">Support ticket</div><h2 className="font-mono uq-modal-title">{ticketReference(selected)}</h2></div>
        <button className="zs-btn zs-btn-secondary zs-btn-sm shrink-0" disabled={sending} onClick={close}><FiX aria-hidden="true" />Close</button>
      </header>
        <div className="uq-modal-body">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6"><TicketStatus query={selected} /><span className="text-xs text-ink-3">Submitted {ticketDate(selected.created_at)}</span></div>
          <TicketChat key={ticketReference(selected)} ticket={selected} onBusy={setSending} onUpdate={updated => {
            setSelected(updated); setQueries(rows => rows.map(row => row.id === updated.id && row.source === updated.source ? updated : row));
          }} />
        </div></>}
    </Modal>
  </div>;
}
